<script setup lang="ts">
import rtgLogo from "@/assets/images/RoomsToGoLogo.png";
import Textbox from "@/components/Textbox.vue";
import { storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import HelpMarker from "../features/help/HelpMarker.vue";
import { getRouteHelpTopicId } from "../features/help/helpTopics.ts";
import {
  matchesBulkOrIntermediateOrderPrefix,
  searchBulkIntermediateOrdersAndNavigate,
} from "../features/home/bulkIntermediateOrderSearch.ts";
import OpenPurchaseOrdersSearchPanel from "../features/home/OpenPurchaseOrdersSearchPanel.vue";
import { normalizeSearchText } from "../features/home/searchTypeClassifier.ts";
import { useTypedPurchaseOrderSearch } from "../features/home/typedPurchaseOrderSearch.ts";
import { useAppStore } from "../stores/app.ts";
import { useModalStore } from "../stores/modalStore.ts";

const appStore = useAppStore();
const { intermediateOrderSearching } = storeToRefs(appStore);
const route = useRoute();
const router = useRouter();
const modalStore = useModalStore();
const { searching, runTypedInputSearch } = useTypedPurchaseOrderSearch();
const headerSearchInput = ref("");
const SEARCH_OPTIONS_DIALOG_ID = "search-options-modal";
const searchOptionsBlockedMessage = ref<string | undefined>(undefined);

const badgeText = computed(() => {
  const parts: string[] = [];

  if (appStore.headerVendorBadgeValue)
    parts.push(`Vendor: ${appStore.headerVendorBadgeValue}`);

  if (appStore.headerDivisionBadgeValue) {
    const divisionCount = appStore.headerDivisionBadgeValue
      .split(",")
      .map((value) => value.trim())
      .filter((value) => value.length > 0).length;
    parts.push(
      `${divisionCount > 1 ? "Divisions" : "Division"}: ${appStore.headerDivisionBadgeValue}`
    );
  }

  if (appStore.headerWarehouseBadgeValue) {
    const warehouseCount = appStore.headerWarehouseBadgeValue
      .split(",")
      .map((value) => value.trim())
      .filter((value) => value.length > 0).length;
    parts.push(
      `${warehouseCount > 1 ? "Warehouses" : "Warehouse"}: ${appStore.headerWarehouseBadgeValue}`
    );
  }

  return parts.length > 0 ? parts.join(" | ") : undefined;
});

// Title and subtitle come from route meta so every page owns its own header
// copy. appStore.headerTitle stays as the fallback for routes without meta.
const headerTitle = computed(() => {
  const metaTitle = route.meta.pageTitle;
  return typeof metaTitle === "string" ? metaTitle : appStore.headerTitle;
});

const headerSubtitle = computed(() => {
  const metaSubtitle = route.meta.pageSubtitle;
  return typeof metaSubtitle === "string" ? metaSubtitle : undefined;
});

// Every route declares its own help topic in meta, so adding a page's help is a
// one-line router change rather than an edit here.
const headerHelpTopicId = computed(() => getRouteHelpTopicId(route.meta));

const PO_SEARCH_ROUTES = new Set(["search-select-pos", "calc", "results"]);
const BULK_ORDER_CREATION_ROUTES = new Set([
  "dashboard-bulk-order-creation",
  "dashboard-bulk-order-creation-calculate",
]);
const hideBulkOrderHeaderSearch = computed(() =>
  BULK_ORDER_CREATION_ROUTES.has(String(route.name ?? ""))
);
const isIntermediateOrderRoute = computed(
  () => route.name === "dashboard-intermediate-order-sku-distribution"
);
// The search renders on every page. Off the PO workflow there is nowhere to
// show results, so submitting navigates to PO Recommendations and runs there.
const runsSearchInPlace = computed(
  () =>
    PO_SEARCH_ROUTES.has(String(route.name ?? "")) ||
    isIntermediateOrderRoute.value
);

const normalizedHeaderSearchInput = computed(() =>
  normalizeSearchText(headerSearchInput.value)
);

// Case-insensitive: "Z..." (Bulk Order) or "IPO..." (Intermediate PO) both
// route to the same bulk/intermediate order search, from any page.
const inputMatchesBulkOrIntermediateOrderPrefix = computed(() =>
  matchesBulkOrIntermediateOrderPrefix(headerSearchInput.value)
);

const activeSearching = computed(() =>
  isIntermediateOrderRoute.value
    ? intermediateOrderSearching.value
    : searching.value
);

const headerSearchLabel = computed(() =>
  isIntermediateOrderRoute.value ||
  inputMatchesBulkOrIntermediateOrderPrefix.value
    ? "Find Bulk/Intermediate Orders"
    : "Find Related POs"
);
const headerSearchPlaceholder = computed(() =>
  isIntermediateOrderRoute.value ? "Enter BO#/IO#" : "Enter PO#, or Bulk/IPO#"
);

const canSubmitHeaderSearch = computed(() => {
  return normalizedHeaderSearchInput.value.length > 0 && !activeSearching.value;
});

watch(activeSearching, (isSearching) => {
  if (!isSearching) {
    searchOptionsBlockedMessage.value = undefined;
  }
});

async function submitHeaderSearch(): Promise<void> {
  if (!canSubmitHeaderSearch.value) {
    return;
  }

  if (
    inputMatchesBulkOrIntermediateOrderPrefix.value ||
    isIntermediateOrderRoute.value
  ) {
    await searchBulkIntermediateOrdersAndNavigate({
      query: headerSearchInput.value,
      appStore,
      router,
      route,
    });
    return;
  }

  // Navigate before searching so the results grid is mounted and in its loading
  // state by the time results land, rather than reacting mid-transition.
  if (!runsSearchInPlace.value) {
    await router.push({ name: "search-select-pos" });
  }

  await runTypedInputSearch(headerSearchInput.value);
}

function openSearchOptionsModal(): void {
  if (activeSearching.value) {
    searchOptionsBlockedMessage.value = "search in process, please wait";
    return;
  }

  if (modalStore.isOpen(SEARCH_OPTIONS_DIALOG_ID)) {
    return;
  }

  searchOptionsBlockedMessage.value = undefined;
  if (route.name === "dashboard-intermediate-order-sku-distribution") {
    appStore.activeSearchTab = "bulk-order-lookup";
  }
  modalStore.show(OpenPurchaseOrdersSearchPanel, {
    dialogId: SEARCH_OPTIONS_DIALOG_ID,
    width: 1100,
    height: 365,
    closeOnOverlayClick: true,
    componentProps: {
      showTabs: true,
      showModalChrome: true,
    },
  });
}
</script>

<template>
  <header class="app-header-header">
    <div class="app-header-inner">
      <div class="app-header-brand">
        <h1 class="app-header-title">
          {{ headerTitle }}
          <HelpMarker v-if="headerHelpTopicId" :topic-id="headerHelpTopicId" />
        </h1>
        <span
          v-if="headerSubtitle"
          class="app-header-subtitle mt-0.5 text-[12px]"
          >{{ headerSubtitle }}</span
        >
      </div>
      <div v-if="!hideBulkOrderHeaderSearch" class="app-header-middle">
        <div class="app-header-search-shell">
          <div class="app-header-search-field">
            <Textbox
              v-model="headerSearchInput"
              class="app-header-search-input"
              :placeholder="headerSearchPlaceholder"
              :label="headerSearchLabel"
              :value-transformer="(value) => normalizeSearchText(value ?? '')"
              @keydown.enter.prevent="submitHeaderSearch"
            />
            <button
              class="app-header-search-button"
              type="button"
              :disabled="!canSubmitHeaderSearch"
              aria-label="Search purchase orders"
              @click="submitHeaderSearch"
            >
              <svg
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
                class="h-4.5 w-4.5"
              >
                <circle
                  cx="8.75"
                  cy="8.75"
                  r="5.75"
                  stroke="currentColor"
                  stroke-width="1.6"
                />
                <path
                  d="M13.2 13.2 17 17"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                />
              </svg>
            </button>
          </div>
          <button
            class="app-header-search-options-link"
            type="button"
            aria-haspopup="dialog"
            :aria-disabled="activeSearching"
            @click="openSearchOptionsModal"
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
              class="app-header-search-options-icon"
            >
              <path
                d="M4.5 4.5h7.2a3.8 3.8 0 0 1 3.8 3.8v7.2"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
              />
              <path
                d="M9.2 9.2 15.5 2.9M13.2 2.9h2.3v2.3"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            Vendor Search / Bulk PO Search
          </button>
          <p
            v-if="searchOptionsBlockedMessage"
            class="app-header-search-options-status"
            role="status"
            aria-live="polite"
          >
            {{ searchOptionsBlockedMessage }}
          </p>
        </div>
      </div>
      <div class="app-header-actions">
        <span v-if="badgeText" class="app-header-badge">{{ badgeText }}</span>
        <RouterLink
          :to="{ name: 'search-select-pos' }"
          class="app-header-home-link inline-flex items-center"
          aria-label="Search & Select POs"
        >
          <img class="h-10 w-auto" :src="rtgLogo" alt="RoomsToGo Logo" />
        </RouterLink>
      </div>
    </div>
  </header>
</template>
